// apps/app/src/components/state-banner.tsx
//
// Tenant state, said plainly. Derived from one field — never from scattered
// feature flags — so "can we scan right now?" has exactly one answer.

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { http } from '@/lib/api-client';
import { keys } from '@/lib/query-client';
import { useAuth, type TenantState } from '@/lib/auth';
import { cn } from '@/lib/utils';

interface Overview {
  state: TenantState;
  usage: { scans: number; limit: number; remaining: number; percentUsed: number };
  trialStartedAt: string | null;
}

// Text keys are in the common namespace (`banner.<key>.title|body|action`),
// translated where the banner renders.
const BANNERS: Partial<Record<TenantState, { tone: string; key: string; action?: boolean }>> = {
  TEST: {
    tone: 'bg-amber-50 border-amber-200 text-amber-900',
    key: 'test',
    action: true,
  },
  PAST_DUE: {
    tone: 'bg-orange-50 border-orange-200 text-orange-900',
    // Still scanning during dunning — cutting a paying clinic off over one
    // failed retry loses far more than the unpaid month.
    key: 'pastDue',
    action: true,
  },
  DORMANT: {
    tone: 'bg-stone-100 border-stone-300 text-stone-800',
    key: 'dormant',
    action: true,
  },
  CANCELLED: {
    tone: 'bg-red-50 border-red-200 text-red-900',
    key: 'cancelled',
    action: true,
  },
};

export function StateBanner() {
  const user = useAuth((s) => s.user);
  const { t } = useTranslation('common');

  const { data } = useQuery({
    queryKey: keys.billing,
    queryFn: () => http.get<Overview>('/billing'),
    enabled: Boolean(user?.tenantId),
  });

  if (!data) return null;

  const banner = BANNERS[data.state];

  // Quota gets its own warning, independent of state — an ACTIVE clinic at 90%
  // needs to know before their scanner stops, not after.
  const quotaWarning = data.usage.limit > 0 && data.usage.percentUsed >= 80;

  if (!banner && !quotaWarning) return null;

  return (
    <div className="px-[26px] pt-4">
      {banner && (
        <Alert className={cn('flex items-center justify-between gap-4 rounded-xl', banner.tone)}>
          <div className="min-w-0">
            <AlertTitle className="font-semibold">{t(`banner.${banner.key}.title`)}</AlertTitle>
            <AlertDescription className="opacity-90">{t(`banner.${banner.key}.body`)}</AlertDescription>
          </div>
          {banner.action && (
            <Button asChild variant="outline" size="sm" className="shrink-0 border-black/10 bg-transparent text-inherit hover:bg-black/5 hover:text-inherit">
              <Link to="/settings/billing">{t(`banner.${banner.key}.action`)}</Link>
            </Button>
          )}
        </Alert>
      )}

      {quotaWarning && (
        <Alert className="mt-2 rounded-xl border-amber-200 bg-amber-50 text-amber-900">
          <AlertDescription>
            {t('banner.quota', { used: data.usage.scans, limit: data.usage.limit })}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
